import { arktypeResolver } from '@hookform/resolvers/arktype';
import { Alert, Box, Collapse, TextField } from '@mui/material';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Navigate, useParams } from 'react-router';

import { useCreateRecipientMutation } from '@/api/v1';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { useCustomer } from '@/hooks/useCustomer';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  CryptoNetworkType,
  CryptoPaymentForm,
  cryptoPaymentMethodSchema,
} from '@/schemas/cryptoPaymentMethod';
import { CryptoAccountDetailFields } from '@/Screens/Dashboard/Contacts/CryptoAccountDetailFields';
import { Contact, SelfContact } from '@/types/contacts';
import { isCompanyContact, isIndividualContact } from '@/types/contacts.ext';
import { isCountryType } from '@/types/country.ext';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { PaymentMethodAccountType } from '@/types/paymentMethodAccountType';
import { isFetchError } from '@/utility/reactToolkit';

interface Props {
  readonly redirectPath: string;
  readonly contact: Contact | SelfContact | undefined;

  onCreated(id: string): void;
}

const OTHER_VASP_ID = '__OTHER__';

const emptyCryptoForm: CryptoPaymentForm = {
  type: PaymentMethodAccountType.crypto,
  paymentMethod: FiatAccountType.crypto,
  network: CryptoNetworkType.ETH,
  walletAddress: '',
  vaspId: '',
  vaspName: '',
  isNonCustodialWallet: false,
};

export const PaymentMethodCrypto: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onCreated, redirectPath, contact } = props;
  const customer = useCustomer();
  const tr = useTranslator();
  const toast = useToast();
  const { country, accountType } = useParams();

  const [createRecipient, mutation] = useCreateRecipientMutation();
  const [nickname, setNickname] = React.useState<string>('');
  const NICKNAME_MAX_LENGTH = 40;

  const onNicknameChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setNickname(event.target.value);
  }, []);

  const form = useFormWithErrors<CryptoPaymentForm>({
    defaultValues: emptyCryptoForm,
    resolver: arktypeResolver(cryptoPaymentMethodSchema),
  });

  const { clearErrors, formState, handleSubmit, setError, watch } = form;

  const onSubmit = React.useCallback(
    async (formData: CryptoPaymentForm): Promise<void> => {
      if (!customer.beneficiaryId) {
        console.warn('missing beneficiaryId');
        return;
      }
      if (!contact) {
        console.warn('missing contact');
        return;
      }

      const isOtherVasp = formData.vaspId === OTHER_VASP_ID;
      if (!formData.isNonCustodialWallet && !formData.vaspId) {
        setError('vaspId', { type: 'manual', message: tr('VALIDATION_REQUIRED') });
        return;
      }
      if (isOtherVasp && !formData.vaspName.trim()) {
        setError('vaspName', { type: 'manual', message: tr('VALIDATION_REQUIRED') });
        return;
      }

      const nicknameToSave = nickname.trim() || null;
      if (nicknameToSave !== null && !/^[a-zA-Z0-9\s\-_]+$/.test(nicknameToSave)) {
        toast.show(tr('NICKNAME_INVALID_CHARACTERS'), 'error');
        return;
      }

      const currency =
        formData.network === CryptoNetworkType.ETH ? CurrencyCodeType.USDC : CurrencyCodeType.USDT;
      const railType =
        formData.network === CryptoNetworkType.ETH
          ? PaymentRailType.Ethereum
          : PaymentRailType.TRON;

      const getIndividualName = (): string | undefined => {
        if (contact && isIndividualContact(contact.entity)) {
          return `${contact.entity.firstName} ${contact.entity.lastName}`;
        }
        return undefined;
      };

      const getBusinessName = (): string | undefined => {
        if (contact && isCompanyContact(contact.entity)) {
          return contact.entity.legalEntityName;
        }
        return undefined;
      };

      const getCountryOfIncorporation = (): string | undefined => {
        if (isCompanyContact(contact.entity)) {
          if ('country' in contact) {
            return (contact as Contact).country;
          }
          return (
            customer?.businessInfo?.country ?? customer?.businessInfo?.address?.country ?? undefined
          );
        }
        return undefined;
      };

      const details: Record<string, unknown> = {
        walletAddress: formData.walletAddress,
        isNonCustodialWallet: formData.isNonCustodialWallet,
      };

      if (!formData.isNonCustodialWallet) {
        if (isOtherVasp) {
          details.vaspName = formData.vaspName.trim();
        } else if (formData.vaspId) {
          details.vaspId = formData.vaspId;
        }
      }

      try {
        const result = await createRecipient({
          beneficiaryId: customer.beneficiaryId,
          individualName: getIndividualName(),
          businessName: getBusinessName(),
          currency: currency,
          type: railType,
          contactId: contact.id ?? undefined,
          belongsToBeneficiary: contact.id === null,
          nickname: nicknameToSave,
          countryOfIncorporation: getCountryOfIncorporation(),
          details: details,
        }).unwrap();
        onCreated(result.id);
      } catch (error: unknown) {
        if (isFetchError(error) && error.data.code === 'recipient.validation_error') {
          const detail = error.data.message.replace(/^Recipient validation error for\s*/i, '');
          setError('root', { type: 'manual', message: detail });
        } else {
          toast.show(tr('AN_ERROR_OCCURRED'), 'error');
          console.warn(error);
        }
      }
    },
    [contact, createRecipient, customer, nickname, onCreated, setError, toast, tr],
  );

  React.useEffect((): (() => void) => {
    const subscription = watch((): void => {
      if (formState.errors.root) {
        clearErrors('root');
      }
    });
    return (): void => subscription.unsubscribe();
  }, [watch, clearErrors, formState.errors.root]);

  if (!isCountryType(country) || accountType !== FiatAccountType.crypto) {
    return <Navigate to={redirectPath} replace={true} />;
  }

  return (
    <Box component="form" name="payment-method-crypto" onSubmit={handleSubmit(onSubmit)}>
      <Box mt={2}>
        <CryptoAccountDetailFields form={form} />
        <Box mt={4}>
          <TextField
            label={tr('NICKNAME_OPTIONAL')}
            placeholder={tr('NICKNAME_PLACEHOLDER')}
            value={nickname}
            onChange={onNicknameChange}
            inputProps={{ maxLength: NICKNAME_MAX_LENGTH }}
            helperText={`${nickname.length}/${NICKNAME_MAX_LENGTH}`}
            fullWidth
          />
        </Box>
        <Collapse in={!!formState.errors.root?.message}>
          <Box mt={4}>
            <Alert severity="error">{formState.errors.root?.message}</Alert>
          </Box>
        </Collapse>
        <Box mt={6}>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth={true}
            loading={mutation.status === QueryStatus.pending}>
            {tr('ADD_CONTACT')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
